import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getMediaUrl } from '../../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANON_KEY = 'classroom_anon_id';
const getOrCreateAnonId = () => {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = `anon_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
};

const formatRange = (startIso, endIso) => {
  if (!startIso || !endIso) return '';
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
};

/**
 * "This Week's Story" — same premium light surface as the daily story page,
 * but with a wider editorial frame and up to 5 supporting moments. Reuses
 * `_pick_week_story()` server-side for variety across days.
 */
const WeekStoryPage = () => {
  const { shareCode } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/share/classroom/${shareCode}/week`);
        setStory(res.data || null);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load this week');
      } finally {
        setLoading(false);
      }
    })();
    // Attribution ping (forward vs direct + optional upload_id) — see
    // TodayStoryPage for rationale.
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get('ref') === 'forward' ? 'forward' : 'direct';
      const uploadId = params.get('u') || null;
      axios
        .post(`${API}/share/classroom/${shareCode}/story-view`, {
          kind: 'week',
          source,
          anon_id: getOrCreateAnonId(),
          ...(uploadId ? { upload_id: uploadId } : {}),
        })
        .catch(() => {});
    } catch { /* ignore — analytics must never break the page */ }
  }, [shareCode]);

  const onShare = async () => {
    const url = window.location.href;
    const text = `${story?.workspace?.name} — This Week's Story`;
    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — paste it anywhere ❤️');
    } catch {
      toast.error('Could not copy — share the link manually');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!story?.workspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-black">
        <div className="text-sm text-gray-500">Link not found.</div>
      </div>
    );
  }

  const { workspace, picks, week_starting, week_ending, count_total, forward_count, most_shared_upload_id } = story;
  const hero = picks[0];
  const supporting = picks.slice(1);

  // Social proof whisper — only when ≥3 forwards in the week (per spec).
  // 3-5 stays vague ("a few parents"); 6+ shows the exact count.
  let socialProof = null;
  if (forward_count >= 6) {
    socialProof = `👀 Shared with family by ${forward_count} parents this week`;
  } else if (forward_count >= 3) {
    socialProof = '👀 Shared with family by a few parents this week';
  }

  // Parent-side whisper ribbon for the most-shared moment lifetime — only
  // surfaces on the single matching tile (hero or supporting). Backend
  // already enforced the ≥5 threshold; here we just check identity.
  const ribbonForUpload = (uploadId) =>
    most_shared_upload_id && uploadId === most_shared_upload_id;

  if (!hero) {
    return (
      <div className="min-h-screen bg-white text-black" data-testid="week-story-empty">
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-4">
            This Week's Story
          </p>
          <h1 className="text-3xl font-light tracking-tight mb-3">
            A quieter week.
          </h1>
          <p className="text-base text-gray-500 leading-relaxed mb-8">
            Not enough moments yet to tell this week's story — check back next Sunday.
          </p>
          <Link
            to={`/c/${shareCode}`}
            className="inline-block text-sm font-semibold underline underline-offset-4"
            data-testid="week-story-back"
          >
            See past collections →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black" data-testid="week-story-page">
      <article className="max-w-md mx-auto px-5 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <figure
          className="relative w-full overflow-hidden mb-8 hero-fade-in"
          style={{
            aspectRatio: '4 / 5',
            borderRadius: '16px',
            boxShadow: '0 20px 60px -20px rgba(0,0,0,0.25)',
          }}
          data-testid="week-story-hero"
        >
          <img
            src={getMediaUrl(hero, { size: 'thumb_800' })}
            alt=""
            className="w-full h-full object-cover hero-zoom"
          />
          {ribbonForUpload(hero.upload_id) && (
            <p
              className="absolute top-3 left-3 z-10 text-xs tracking-wide pointer-events-none select-none"
              style={{
                color: 'rgba(212, 175, 55, 0.92)',
                textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                letterSpacing: '0.04em',
              }}
              data-testid="week-story-most-loved-ribbon-hero"
            >
              ⭐ One of this term's most-loved moments
            </p>
          )}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 45%)',
            }}
          />
          <figcaption className="absolute bottom-0 left-0 right-0 px-5 pb-5 pt-10 text-white">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-80 mb-1.5">
              This Week in {workspace.name}
            </div>
            <div className="text-sm opacity-90">{formatRange(week_starting, week_ending)}</div>
          </figcaption>
        </figure>

        {/* Title block */}
        <header className="mb-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400 mb-3">
            📅 This Week's Story
          </p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight leading-tight text-gray-900">
            A week of moments together.
          </h1>
          {socialProof && (
            <p
              className="mt-4 text-sm text-gray-500"
              data-testid="week-story-social-proof"
            >
              {socialProof}
            </p>
          )}
        </header>

        {/* Supporting — 2-col grid for up to 4; if exactly 1 supporting, full width */}
        {supporting.length > 0 && (
          <div
            className={`grid gap-2 mb-10 ${
              supporting.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
            }`}
            data-testid="week-story-supporting"
          >
            {supporting.map((s) => (
              <a
                key={s.upload_id}
                href={getMediaUrl(s.storage_path, null)}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block overflow-hidden"
                style={{
                  aspectRatio: '4 / 5',
                  borderRadius: '12px',
                }}
                data-testid={`week-story-supporting-${s.upload_id}`}
              >
                <img
                  src={getMediaUrl(s, { size: 'thumb_800' })}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                />
                {ribbonForUpload(s.upload_id) && (
                  <span
                    className="absolute top-2 left-2 z-10 text-[10px] tracking-wide pointer-events-none select-none"
                    style={{
                      color: 'rgba(212, 175, 55, 0.92)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                      letterSpacing: '0.04em',
                    }}
                    data-testid={`week-story-most-loved-ribbon-${s.upload_id}`}
                  >
                    ⭐ One of this term's most-loved moments
                  </span>
                )}
              </a>
            ))}
          </div>
        )}

        {/* CTA */}
        <Link
          to={`/c/${shareCode}`}
          className="block w-full text-center text-white font-semibold text-[15px] tracking-tight py-3.5 px-6 rounded-full transition-transform hover:scale-[1.015]"
          style={{ background: '#15161B' }}
          data-testid="week-story-view-all"
        >
          View all collections
          <ArrowRight size={14} weight="bold" className="inline ml-2 -mt-0.5" />
        </Link>

        {/* Share */}
        <button
          type="button"
          onClick={onShare}
          className="mt-5 w-full text-center text-sm text-gray-500 hover:text-gray-900 transition-colors flex items-center justify-center gap-2"
          data-testid="week-story-share"
        >
          <ShareNetwork size={14} weight="bold" />
          Share this week with family ❤️
        </button>

        {count_total > picks.length && (
          <p
            className="mt-10 text-center text-[11px] uppercase tracking-[0.22em] font-bold text-gray-400"
            data-testid="week-story-meta"
          >
            Curated from {count_total} moments this week
          </p>
        )}
      </article>

      <style>{`
        @keyframes heroFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroZoom {
          from { transform: scale(1.02); }
          to   { transform: scale(1); }
        }
        .hero-fade-in { animation: heroFadeIn 700ms ease-out both; }
        .hero-zoom    { animation: heroZoom 1400ms ease-out both; transform-origin: center; }
      `}</style>
    </div>
  );
};

export default WeekStoryPage;
